import { useCallback, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import type { CompositionNode, CompositionRegion, RegionRef } from '../../model/composition'
import type { Manifest } from '../../api/authoring'
import { useTargetDocument } from '../../api/useTargetDocument'
import { typeOf } from '../../model/manifest'
import { samePath, type LinkPath } from '../../model/link'
import { DragHandle } from '../../ui/DragHandle'
import { RowMenu } from '../../ui/RowMenu'
import { moved, type Reorder } from '../../ui/reorder'
import type { Arranging } from '../page/Composition'

/**
 * The blocks on a page, in reading order.
 *
 * **The same tree the composition control draws, with the model taken
 * off it.** No link ids, no pin chips, no target ids, no undeclared-slot
 * warnings, no "arrangements this client cannot draw" — every one of
 * those is a true and useful thing to say to somebody holding the
 * component model, and noise to somebody who wants to change the words
 * on their homepage.
 *
 * What is left is a name, an order, and a way in. The name is the type's
 * own prose — "Rich text block", "Photo gallery" — which is the whole reason
 * a type declares any: a row reading `Gallery` is the wire showing
 * through, and a row reading `custom/practice` is unreadable.
 *
 * **The way in opens here.** A selected row is drawn with its editor
 * beneath it, and the tree still knows nothing about what an editor is —
 * `renderEditor` is handed down by the screen that resolved the
 * selection, so every query and every command address stays where it
 * already was. A tree that loaded the selected block itself would be the
 * second model this client is not allowed to grow.
 */
export function Blocks({
  node,
  region,
  tenant,
  manifest,
  arrangingFor,
  titleOf,
  previewOf,
  dragging,
  groupOf,
  selection,
  onSelect,
  onAdd,
  onReorder,
  onRemove,
  renderEditor,
  busy,
}: {
  node: CompositionNode
  region: CompositionRegion
  tenant: string
  manifest: Manifest | undefined
  /**
   * What may be done to one node's region, resolved by the caller.
   *
   * A function rather than a value because the answer differs down the
   * tree: a page's blocks are placed by the page's intents and a
   * section's by the section's, and both are drawn here at once.
   */
  arrangingFor: (node: CompositionNode, region: CompositionRegion) => Arranging
  titleOf: (typeName: string) => string
  /** The authored words to show for a node, in reading order. */
  previewOf: (node: CompositionNode) => string[]
  /**
   * The drag, owned by the page and shared by every region on it.
   *
   * A block carried from the header into the body is a drop only
   * something watching both ends can see, so there is one hook for the
   * page rather than one per region — and whether the drop is allowed
   * comes from the manifest, not from here.
   */
  dragging: Reorder<RegionRef>
  groupOf: (node: CompositionNode, region: CompositionRegion) => string
  selection: LinkPath
  onSelect: (path: LinkPath) => void
  onAdd: (node: CompositionNode, region: CompositionRegion) => void
  onReorder: (node: CompositionNode, region: CompositionRegion, order: string[]) => void
  onRemove: (node: CompositionNode, region: CompositionRegion, placement: CompositionNode) => void
  /**
   * The selected block's editor, drawn under its own row.
   *
   * A render prop rather than a component, because the selection can be
   * at any depth and this control is recursive: the screen answers for
   * one placement and every level asks the same question. It returns
   * nothing for a row that is not the selected one.
   */
  renderEditor?: (placement: CompositionNode) => ReactNode
  busy: boolean
}) {
  const order = region.linkIds
  const arranging = arrangingFor(node, region)
  const group = groupOf(node, region)
  const ref: RegionRef = { node, region }

  // A row that becomes the selected one is brought into view, and
  // `nearest` means a row already on screen does not move at all. It is
  // a callback ref rather than an effect because React hands it the node
  // exactly when the row *becomes* selected — the ref is attached to one
  // row at a time, so a re-render of a row that was already selected
  // does not scroll a reader who is reading.
  const reveal = useCallback((row: HTMLElement | null) => {
    row?.scrollIntoView({ block: 'nearest' })
  }, [])

  const step = (linkId: string, direction: -1 | 1) => {
    const from = order.indexOf(linkId)
    const target = order[from + direction]
    if (from < 0 || target === undefined) return
    const next = moved(order, linkId, target)
    if (next) onReorder(node, region, next)
  }

  return (
    <Box>
      {region.placements.length === 0 && (
        <Typography
          {...dragging.zoneProps(group, order, ref)}
          variant="body2"
          color="text.secondary"
          sx={{
            px: 2,
            py: 2.5,
            bgcolor: dragging.landingIn(group) ? 'action.hover' : undefined,
            outline: dragging.landingIn(group) ? 2 : 0,
            outlineColor: 'primary.main',
          }}
        >
          Nothing here yet.
        </Typography>
      )}

      {region.placements.map((placement, index) => {
        const linkId = placement.linkId!
        const preview = previewOf(placement)
        // Draggable where the blocks can be rearranged *or* carried into
        // another part of the page. A single block in the header is
        // still worth picking up when it can go into the body.
        const movable = Boolean(arranging.arrange) || Boolean(arranging.move)
        const at = order.indexOf(linkId)
        const drag = movable ? dragging.rowProps(group, linkId, order, ref) : {}
        const landing = movable ? dragging.landingOn(group, linkId) : undefined
        const selected = samePath(selection, placement.path)

        return (
          <Box key={linkId}>
            {index > 0 && <Divider />}
            <Stack
              {...drag}
              ref={selected ? reveal : undefined}
              direction="row"
              spacing={1.5}
              alignItems="center"
              // Clicking the selected row again closes it. The row is
              // the control that opened the editor, so it is the one
              // that ought to shut it.
              onClick={() => onSelect(selected ? [] : placement.path)}
              sx={{
                px: 2,
                py: 1.5,
                pl: 2 + placement.depth * 2,
                cursor: 'pointer',
                opacity: movable && dragging.isHeld(group, linkId) ? 0.4 : 1,
                borderTop: landing === 'above' ? 2 : 0,
                borderBottom: landing === 'below' ? 2 : 0,
                borderColor: 'primary.main',
                bgcolor: selected ? 'action.selected' : undefined,
                '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
              }}
            >
              {movable && (
                <DragHandle label={arranging.arrange?.label ?? arranging.move?.label ?? 'Move'} disabled={busy} />
              )}

              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                {preview.length > 0 ? (
                  <>
                    {preview.map((text, valueIndex) => (
                      <Typography
                        key={`${valueIndex}-${text}`}
                        variant={valueIndex === 0 ? 'body1' : 'body2'}
                        color={valueIndex === 0 ? 'text.primary' : 'text.secondary'}
                        sx={{ fontWeight: valueIndex === 0 ? 500 : undefined }}
                        noWrap
                      >
                        {text}
                      </Typography>
                    ))}
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {titleOf(placement.type)}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontWeight: 500 }} noWrap>
                    {titleOf(placement.type)}
                  </Typography>
                )}
              </Box>

              {/* A placement whose target has gone is the one piece of
                  model an owner has to be told about, because the page
                  is missing something and nothing else on screen says
                  so. */}
              {placement.linked && <Missing tenant={tenant} id={placement.targetId} />}

              <RowMenu
                disabled={busy}
                actions={[
                  ...(arranging.arrange
                    ? [
                        {
                          label: 'Move up',
                          icon: <ArrowUpwardIcon fontSize="small" />,
                          disabled: at <= 0,
                          onSelect: () => step(linkId, -1),
                        },
                        {
                          label: 'Move down',
                          icon: <ArrowDownwardIcon fontSize="small" />,
                          disabled: at < 0 || at >= order.length - 1,
                          onSelect: () => step(linkId, 1),
                        },
                      ]
                    : []),
                  ...(arranging.act
                    ? [
                        {
                          label: arranging.act.label,
                          icon: <DeleteOutlineIcon fontSize="small" />,
                          destructive: true,
                          detail: 'Takes it off the page. Anything it was made from is kept.',
                          onSelect: () => onRemove(node, region, placement),
                        },
                      ]
                    : []),
                ]}
              />
            </Stack>

            {renderEditor?.(placement)}

            {/* A block that holds blocks holds them here, indented. An
                inline section on a page is a thing an owner made and
                fills, so it cannot be a leaf.

                **What a block will draw is not listed here.** A repeating
                block's records, a pattern's output and a shared block's
                insides are what the Preview tab reads; this tab is the
                blocks an author places and edits on this page. */}
            {!placement.linked &&
              placement.regions.filter((inner) => shownHere(manifest, placement, inner)).map((inner) => (
                <Nested
                  key={`${linkId}-${inner.slot ?? ''}`}
                  node={placement}
                  region={inner}
                  tenant={tenant}
                  manifest={manifest}
                  arrangingFor={arrangingFor}
                  titleOf={titleOf}
                  previewOf={previewOf}
                  dragging={dragging}
                  groupOf={groupOf}
                  selection={selection}
                  onSelect={onSelect}
                  onAdd={onAdd}
                  onReorder={onReorder}
                  onRemove={onRemove}
                  renderEditor={renderEditor}
                  busy={busy}
                />
              ))}
          </Box>
        )
      })}

      {arranging.place && (
        <Stack direction="row" sx={{ px: 2, py: 1.25 }}>
          <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => onAdd(node, region)}>
            {arranging.place.label}
          </Button>
        </Stack>
      )}
    </Box>
  )
}

/**
 * "Missing", where a shared block's target has been deleted or never
 * existed.
 *
 * The page's document says only which component a placement links to,
 * so whether that component is still there is a read of the target —
 * the same point read, keyed the same way, that naming it or opening it
 * makes, so it is shared rather than repeated. The head is read even for
 * a pinned placement: deleting is a fact about the component, not about
 * one of its revisions.
 *
 * A read that failed for any other reason claims nothing either way.
 */
function Missing({ tenant, id }: { tenant: string; id: string | undefined }) {
  const target = useTargetDocument(tenant, id)
  const gone =
    !id ||
    target.data?.tombstoned === true ||
    (target.error !== undefined && 'status' in target.error && target.error.status === 404)
  return gone ? <Chip size="small" color="error" label="Missing" /> : null
}

/**
 * Whether an inner region is drawn on this tab.
 *
 * **A layout's regions always are**, empty or not: a section an owner
 * made is a thing they fill, and its Add button is the way in. A block
 * that can hold others besides being a block in its own right — a link
 * wrapping a picture, a menu's links — draws its region only once it
 * holds something. Every link on a page opening an empty "Inside" with
 * an Add button was a question asked of every link, about a use few of
 * them have; the Structure tab still offers it.
 *
 * Read off the holder's role, so a type this client has never seen
 * follows whichever it declares.
 */
function shownHere(manifest: Manifest | undefined, holder: CompositionNode, region: CompositionRegion): boolean {
  return region.placements.length > 0 || typeOf(manifest, holder.type)?.role !== 'block'
}

/** One inner region, indented under the block that holds it. */
function Nested(props: Parameters<typeof Blocks>[0]) {
  return (
    <Box sx={{ borderLeft: 2, borderColor: 'divider', ml: 3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pt: 1 }}>
        {props.region.slot ?? 'Inside'}
      </Typography>
      <Blocks {...props} />
    </Box>
  )
}
